/* Generated cover art for the catalogue.

   A catalogue that reads like a film library normally rests on photography.
   This one has none and wants none: a photograph would date the material and
   tie it to somebody else's visual world. Every tile is therefore computed —
   geometric, abstract, and deliberately not in any one school.

   What a composition is made of is decided by the branch the reader is standing
   in, not by the individual item. Four levels, four decisions:

     the strand owns the palette      zs1, zs2, gym and odborna each have one
                                      pair of pastel tokens, so a column of the
                                      catalogue holds a single colourity and the
                                      stage is legible before the crumbs are
                                      read.
     the subject owns the motif       history is strata in every year of every
                                      strand. The subject is the series; a motif
                                      that changed underneath it would say the
                                      opposite of what the hierarchy says.
     the year owns the density        the first year of a strand is the sparsest
                                      tile in its column and the last the
                                      densest.
     the id owns the variation        and nothing beyond it: the jitter, which
                                      cell is filled, where the accent falls.

   Neighbouring tiles are therefore relatives rather than strangers, and still
   tell apart.

   Two properties hold throughout:

     deterministic   the same id always yields the same picture, so a subject
                     keeps its face between visits and between machines. There
                     is no Math.random here and there must never be.
     quiet           a tile is a plane with character, not an illustration. Few
                     elements, much air, low contrast. What is read is the title
                     underneath; art that competes with it has misunderstood its
                     job.

   Every function returns a complete <svg> string on a 3:2 viewBox with no fixed
   size; the width comes from CSS. Colours are CSS custom properties, so the art
   follows the palette instead of duplicating it.

   Strings rather than JSX on purpose: the same code renders into a React tree,
   into a PDF built by headless Chrome, and into anything else that can take
   markup. Nothing here touches the DOM. */

import type { IndexEntry, Subject } from "./types";

type Rng = () => number;
interface Palette { a: string; b: string }
interface Opts {
  cls?: string;
  /* 0 = the sparsest tile of the column, 1 = the densest. */
  d?: number;
  after?: ((r: Rng, p: Palette) => string) | null;
}
type Motif = (r: Rng, p: Palette, o: Opts) => string;
type Pt = readonly [number, number];

/* The branch a tile is being seen in. `strand` decides the palette; `years` is
   that strand's own span of years, against which a year is placed to get the
   density. The span is passed rather than assumed because gym runs 1–4 and the
   upper stage of the primary school 6–9: on one shared scale a whole column
   would come out all air and another all ink. */
export interface Branch { strand: string; years?: number[] }

/* Indexing inside the geometry below is bounded by the array's own length,
   so the element is always there. This says so once instead of scattering
   non-null assertions through the drawing code. */
function at<T>(a: readonly T[], i: number): T { return a[i] as T; }


interface ShapeOpts { stroke?: string; sw?: number; op?: number }
interface RectOpts extends ShapeOpts { r?: number; rot?: number }
interface PathOpts extends ShapeOpts { fill?: string; dash?: string }

const W = 300, H = 200;

/* ---------- deterministic randomness ----------

   FNV-1a for the seed, mulberry32 for the stream. Both are small, both are
   stable across engines, and neither needs a dependency. */

function seedOf(s: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function rngOf(seed: number): Rng {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function num(r: Rng, a: number, b: number): number { return a + r() * (b - a); }
function int(r: Rng, a: number, b: number): number { return Math.floor(num(r, a, b + 1)); }
function n2(x: number): number { return Math.round(x * 100) / 100; }

/* ---------- palette ----------

   One pair of tints per strand, fixed. It used to be drawn from the stream,
   which gave every tile its own colouring and made a column of the catalogue a
   patchwork; the branch is the thing a reader is actually in, so the branch is
   what the colour says. Ink carries structure and red is the single accent,
   appearing once per composition — the same rule the rest of the material
   follows. */

const BY_STRAND_TINTS: Record<string, readonly [string, string]> = {
  zs1:     ["--p-peach", "--p-sand"],   /* warmest, for the youngest */
  zs2:     ["--p-sage",  "--p-sky"],
  gym:     ["--p-mauve", "--p-slate"],
  odborna: ["--p-slate", "--p-sand"],
};

/* Deliberately a pair no strand uses: an item outside the four should look
   like what it is rather than borrow a branch it does not belong to. */
const FALLBACK_TINTS: readonly [string, string] = ["--p-sky", "--p-peach"];

const INK = "var(--ink)", RED = "var(--red)", PAPER = "var(--paper-2)";

/* The wash under a motif. One value, because a plane that is a shade lighter on
   one tile than on the next reads as a mistake rather than as variety. */
const WASH = 0.3;

function paletteOf(strandId?: string): Palette {
  const t = (strandId ? BY_STRAND_TINTS[strandId] : undefined) ?? FALLBACK_TINTS;
  return { a: "var(" + at(t, 0) + ")", b: "var(" + at(t, 1) + ")" };
}

/* Where a year sits inside its strand's span, 0 to 1. Without the span — a
   material seen outside any column — the whole of compulsory schooling is the
   scale, which is the honest guess when the branch is unknown. */
function densityOf(y?: number, years?: number[]): number {
  if (y == null || !Number.isFinite(y)) return 0.5;
  const span = years && years.length ? years : [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const lo = Math.min(...span), hi = Math.max(...span);
  if (hi <= lo) return 0.5;
  return Math.min(1, Math.max(0, (y - lo) / (hi - lo)));
}

/* How many of a thing at this density. Never below the low end: a first-year
   tile has to read as the same motif as the fourth-year one beside it. A range
   should be at least as wide as the longest strand — five years and four steps
   means two years of the column come out identical. */
function span(o: Opts, lo: number, hi: number): number {
  return Math.round(lo + (hi - lo) * (o.d ?? 0.5));
}

/* ---------- primitives ---------- */

function rect(x: number, y: number, w: number, h: number, fill?: string, o?: RectOpts): string {
  o = o || {};
  return '<rect x="' + n2(x) + '" y="' + n2(y) + '" width="' + n2(w) + '" height="' + n2(h)
    + '" rx="' + (o.r == null ? 0 : o.r) + '" fill="' + (fill || "none") + '"'
    + (o.stroke ? ' stroke="' + o.stroke + '" stroke-width="' + (o.sw || 1.5) + '"' : "")
    + (o.op != null ? ' opacity="' + o.op + '"' : "")
    + (o.rot ? ' transform="rotate(' + n2(o.rot) + ' ' + n2(x + w / 2) + ' ' + n2(y + h / 2) + ')"' : "")
    + "/>";
}

function circ(cx: number, cy: number, rad: number, fill?: string, o?: ShapeOpts): string {
  o = o || {};
  return '<circle cx="' + n2(cx) + '" cy="' + n2(cy) + '" r="' + n2(rad)
    + '" fill="' + (fill || "none") + '"'
    + (o.stroke ? ' stroke="' + o.stroke + '" stroke-width="' + (o.sw || 1.5) + '"' : "")
    + (o.op != null ? ' opacity="' + o.op + '"' : "") + "/>";
}

function path(d: string, o?: PathOpts): string {
  o = o || {};
  return '<path d="' + d + '" fill="' + (o.fill || "none") + '"'
    + (o.stroke ? ' stroke="' + o.stroke + '" stroke-width="' + (o.sw || 1.5)
        + '" stroke-linecap="round" stroke-linejoin="round"' : "")
    + (o.dash ? ' stroke-dasharray="' + o.dash + '"' : "")
    + (o.op != null ? ' opacity="' + o.op + '"' : "") + "/>";
}

function line(x1: number, y1: number, x2: number, y2: number, stroke: string, o?: PathOpts): string {
  o = o || {};
  return '<line x1="' + n2(x1) + '" y1="' + n2(y1) + '" x2="' + n2(x2) + '" y2="' + n2(y2)
    + '" stroke="' + stroke + '" stroke-width="' + (o.sw || 1.5) + '" stroke-linecap="round"'
    + (o.dash ? ' stroke-dasharray="' + o.dash + '"' : "")
    + (o.op != null ? ' opacity="' + o.op + '"' : "") + "/>";
}

/* a smooth closed curve through points, used by the contour motif */
function closedCurve(pts: Pt[]): string {
  let d = "M" + n2(at(pts, 0)[0]) + " " + n2(at(pts, 0)[1]);
  for (let i = 0; i < pts.length; i++) {
    const p0 = at(pts, i), p1 = at(pts, (i + 1) % pts.length);
    const mx = (p0[0] + p1[0]) / 2, my = (p0[1] + p1[1]) / 2;
    d += " Q" + n2(p0[0]) + " " + n2(p0[1]) + " " + n2(mx) + " " + n2(my);
  }
  return d + "Z";
}

/* ---------- motifs ----------

   Each takes (r, p, opts) and returns SVG elements drawn across the whole
   300x200 plane. Counts come from `span(o, …)`, never from the stream: how many
   elements there are is what the year says, and only where they land is left to
   the id. Keep them to a handful of shapes — a tile is 280px wide on screen and
   anything finer turns to mush. */

const MOTIFS = {

  /* modular grid with a few cells filled — measurement, proof, structure */
  grid: function (r, p, o) {
    const cols = span(o, 5, 9), rows = span(o, 4, 6);
    const cw = W / cols, ch = H / rows;
    let out = "";
    for (let i = 1; i < cols; i++) out += line(i * cw, 0, i * cw, H, INK, { sw: 1, op: .1 });
    for (let j = 1; j < rows; j++) out += line(0, j * ch, W, j * ch, INK, { sw: 1, op: .1 });
    const fills = span(o, 2, 5);
    for (let k = 0; k < fills; k++) {
      const cx = int(r, 0, cols - 1), cy = int(r, 0, rows - 1);
      out += rect(cx * cw, cy * ch, cw, ch, k % 2 ? p.a : p.b, { op: .8 });
    }
    const ax = int(r, 1, cols - 1), ay = int(r, 1, rows - 1);
    out += circ(ax * cw, ay * ch, 4.5, RED);
    return out;
  },

  /* horizontal layers, offset at their edges — time, sediment, period */
  strata: function (r, p, o) {
    const bands = span(o, 4, 8);
    let y = 0, out = "";
    for (let i = 0; i < bands && y < H; i++) {
      const h = num(r, H / bands * .65, H / bands * 1.4);
      out += rect(num(r, -20, 20), y, W + 40, h, i % 2 ? p.a : p.b,
                  { op: i % 3 === 2 ? .45 : .85 });
      y += h;
    }
    const cut = num(r, W * .35, W * .7);
    out += line(cut, 0, cut - num(r, -12, 12), H, RED, { sw: 2 });
    return out;
  },

  /* nested closed curves — terrain, place, elevation */
  contour: function (r, p, o) {
    const cx = num(r, W * .38, W * .62), cy = num(r, H * .38, H * .62);
    const rings = span(o, 3, 7);
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const base: number[] = [];
    for (let a = 0; a < 9; a++) base.push(num(r, .76, 1.24));
    for (let i = rings; i >= 1; i--) {
      const scale = i / rings, pts: Pt[] = [];
      for (let k = 0; k < 9; k++) {
        const ang = (k / 9) * Math.PI * 2;
        pts.push([cx + Math.cos(ang) * 96 * scale * at(base, k),
                  cy + Math.sin(ang) * 66 * scale * at(base, k)]);
      }
      out += path(closedCurve(pts), i === 1
        ? { fill: p.b } : { stroke: INK, sw: 1.4, op: .34 });
    }
    out += circ(cx, cy, 4, RED);
    return out;
  },

  /* concentric arcs and a vector — trajectory, force, orbit */
  orbit: function (r, p, o) {
    const cx = num(r, W * .22, W * .42), cy = num(r, H * .55, H * .78);
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const rings = span(o, 2, 4);
    for (let i = 1; i <= rings; i++) {
      out += circ(cx, cy, 34 * i + num(r, -5, 5), "none", { stroke: INK, sw: 1.3, op: .3 });
    }
    const ang = num(r, -1.2, -.4), rr = 34 * rings;
    out += line(cx, cy, cx + Math.cos(ang) * rr * .78, cy + Math.sin(ang) * rr * .78,
                RED, { sw: 2 });
    out += circ(cx + Math.cos(ang) * rr, cy + Math.sin(ang) * rr, 7, p.b,
                { stroke: INK, sw: 1.2, op: .95 });
    out += circ(cx, cy, 3.5, INK, { op: .8 });
    return out;
  },

  /* nodes and bonds — substance, structure, reaction */
  lattice: function (r, p, o) {
    const cols = span(o, 3, 5), rows = span(o, 2, 4);
    const nodes: Pt[] = [];
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        nodes.push([W / (cols + 1) * (i + 1) + (j % 2 ? 16 : -16) + num(r, -5, 5),
                    H / (rows + 1) * (j + 1) + num(r, -7, 7)]);
      }
    }
    let out = "";
    for (let k = 0; k < nodes.length; k++) {
      const a = at(nodes, k), b: Pt | undefined = nodes[k + 1];
      if (b && (k + 1) % cols !== 0) out += line(a[0], a[1], b[0], b[1], INK, { sw: 1.4, op: .38 });
      const down: Pt | undefined = nodes[k + cols];
      if (down && r() > .5) out += line(a[0], a[1], down[0], down[1], INK, { sw: 1.4, op: .38 });
    }
    const hot = int(r, 0, nodes.length - 1);
    for (let m = 0; m < nodes.length; m++) {
      out += circ(at(nodes, m)[0], at(nodes, m)[1], m === hot ? 7.5 : 6,
                  m === hot ? RED : (m % 2 ? p.a : p.b), { stroke: INK, sw: 1.2, op: .95 });
    }
    return out;
  },

  /* recursive branching — growth, taxonomy, the living thing */
  branch: function (r, p, o) {
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const tips: Pt[] = [], depth = span(o, 3, 4);
    function limb(x: number, y: number, ang: number, len: number, left: number): void {
      const x2 = x + Math.cos(ang) * len, y2 = y + Math.sin(ang) * len;
      out += line(x, y, x2, y2, INK, { sw: Math.max(1, left * .8), op: .62 });
      if (left <= 1) { tips.push([x2, y2]); return; }
      const spread = num(r, .36, .6);
      limb(x2, y2, ang - spread, len * num(r, .64, .76), left - 1);
      limb(x2, y2, ang + spread, len * num(r, .64, .76), left - 1);
    }
    /* A shorter tree grows from a longer first limb: three levels and four have
       to fill the same plane, or density would come out as scale. */
    const len0 = depth >= 4 ? num(r, 52, 60) : num(r, 64, 74);
    limb(W / 2 + num(r, -26, 26), H + 6, -Math.PI / 2 + num(r, -.14, .14), len0, depth);
    for (let i = 0; i < tips.length; i++) {
      out += circ(at(tips, i)[0], at(tips, i)[1], num(r, 3, 6),
                  i === 0 ? RED : p.b, { op: .9 });
    }
    return out;
  },

  /* stacked oscillations — sound, speech, the spoken language */
  wave: function (r, p, o) {
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const lines = span(o, 2, 4);
    for (let i = 0; i < lines; i++) {
      const amp = num(r, 10, 26), freq = num(r, 1.3, 2.4), ph = num(r, 0, 6.28);
      const y0 = H / (lines + 1) * (i + 1);
      let d = "M0 " + n2(y0);
      for (let x = 6; x <= W; x += 6) {
        d += " L" + x + " " + n2(y0 + Math.sin(x / W * Math.PI * 2 * freq + ph) * amp);
      }
      const last = i === lines - 1;
      out += path(d, { stroke: last ? RED : INK, sw: last ? 2 : 1.5, op: last ? 1 : .4 });
    }
    out += rect(0, 0, 4, H, p.b, { op: .9 });
    return out;
  },

  /* interlacing bars — text, syntax, a thing built of parts */
  weave: function (r, p, o) {
    let out = "";
    const bars = span(o, 3, 6);
    for (let i = 0; i < bars; i++) {
      const y = H / bars * i + num(r, 3, 9), h = num(r, 8, 14);
      out += rect(num(r, -10, 16), y, num(r, W * .55, W + 10), h,
                  i % 2 ? p.a : p.b, { r: h / 2, op: .9 });
    }
    const verts = span(o, 1, 3);
    for (let j = 0; j < verts; j++) {
      const x = W / (verts + 1) * (j + 1) + num(r, -10, 10);
      out += rect(x, num(r, -10, 10), num(r, 7, 11), H + 20, INK,
                  { r: 6, op: j === 0 ? .62 : .22 });
    }
    out += rect(num(r, 24, W - 60), num(r, 24, H - 44), 30, 6, RED, { r: 3 });
    return out;
  },

  /* boxes and directed edges — process, data, an algorithm */
  flow: function (r, p, o) {
    const cnt = span(o, 3, 4);
    const boxes: number[][] = [];
    for (let i = 0; i < cnt; i++) {
      const bw = num(r, 52, 72), bh = num(r, 24, 32);
      boxes.push([num(r, 14, W - bw - 14), H / cnt * i + num(r, 8, 18), bw, bh]);
    }
    let out = "";
    for (let k = 0; k < boxes.length - 1; k++) {
      const a = at(boxes, k), b = at(boxes, k + 1);
      const ax = at(a, 0) + at(a, 2) / 2, ay = at(a, 1) + at(a, 3);
      const bx = at(b, 0) + at(b, 2) / 2, by = at(b, 1);
      out += path("M" + n2(ax) + " " + n2(ay) + " L" + n2(ax) + " " + n2((ay + by) / 2)
                + " L" + n2(bx) + " " + n2((ay + by) / 2) + " L" + n2(bx) + " " + n2(by),
                  { stroke: INK, sw: 1.5, op: .45 });
    }
    const hot = int(r, 0, boxes.length - 1);
    for (let m = 0; m < boxes.length; m++) {
      const box = at(boxes, m);
      out += rect(at(box, 0), at(box, 1), at(box, 2), at(box, 3),
                  m === hot ? RED : (m % 2 ? p.a : p.b),
                  { r: 6, stroke: INK, sw: 1.3, op: .95 });
    }
    return out;
  },

  /* orthogonal traces and pads — the wired, measured, connected thing */
  circuit: function (r, p, o) {
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const runs = span(o, 3, 6);
    for (let i = 0; i < runs; i++) {
      const y = H / (runs + 1) * (i + 1) + num(r, -6, 6);
      const turn = num(r, W * .32, W * .68), y2 = y + num(r, -28, 28);
      const red = i === runs - 1;
      out += path("M0 " + n2(y) + " L" + n2(turn) + " " + n2(y)
                + " L" + n2(turn + 14) + " " + n2(y2) + " L" + W + " " + n2(y2),
                  { stroke: red ? RED : INK, sw: red ? 2 : 1.5, op: red ? 1 : .42 });
      out += circ(turn, y, 4, red ? RED : p.b, { stroke: INK, sw: 1, op: .95 });
    }
    const pads = span(o, 1, 3);
    for (let k = 0; k < pads; k++) {
      out += rect(num(r, 20, W - 50), num(r, 16, H - 40), 30, 22, "none",
                  { r: 4, stroke: INK, sw: 1.4, op: .45 });
    }
    return out;
  },

  /* ballistic arcs — movement of a body through space */
  arcs: function (r, p, o) {
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const cnt = span(o, 2, 4);
    for (let i = 0; i < cnt; i++) {
      const x0 = num(r, 6, 38), x1 = num(r, W * .6, W - 6), top = num(r, 18, 70);
      const red = i === cnt - 1;
      out += path("M" + n2(x0) + " " + H + " Q" + n2((x0 + x1) / 2) + " " + n2(top - 90)
                + " " + n2(x1) + " " + H,
                  { stroke: red ? RED : INK, sw: red ? 2 : 1.4,
                    op: red ? 1 : .36, dash: red ? "" : "7 6" });
      out += circ((x0 + x1) / 2, top - 8, red ? 6 : 4.5, red ? RED : p.b, { op: .95 });
    }
    return out;
  },

  /* overlapping rotated planes — composition, colour, the made image */
  planes: function (r, p, o) {
    const cnt = span(o, 2, 4), fills = [p.a, p.b, INK];
    let out = "";
    for (let i = 0; i < cnt; i++) {
      out += rect(num(r, -14, W * .55), num(r, -14, H * .5),
                  num(r, 96, 160), num(r, 74, 126), at(fills, i % fills.length),
                  { r: 8, rot: num(r, -20, 20), op: i === 0 ? .9 : .5 });
    }
    /* The accent is a mark of its own rather than the fourth plane: at the
       sparse end of the column there is no fourth plane and the red, which
       every composition owes the reader exactly once, would drop out. */
    out += rect(num(r, W * .56, W - 56), num(r, H * .58, H - 30), 44, 10, RED,
                { r: 5, rot: num(r, -10, 10) });
    return out;
  },

  /* a trace with one spike — the body, the reading, the alarm */
  pulse: function (r, p, o) {
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const y = H * .58, sx = num(r, W * .34, W * .5), halos = span(o, 1, 3);
    for (let i = 0; i < halos; i++) {
      out += circ(sx, y, 24 + i * num(r, 16, 22), "none", { stroke: INK, sw: 1.2, op: .22 });
    }
    out += path("M0 " + n2(y) + " L" + n2(sx - 42) + " " + n2(y)
              + " L" + n2(sx - 26) + " " + n2(y + 16)
              + " L" + n2(sx - 8) + " " + n2(y - 54)
              + " L" + n2(sx + 12) + " " + n2(y + 28)
              + " L" + n2(sx + 30) + " " + n2(y)
              + " L" + W + " " + n2(y), { stroke: RED, sw: 2.2 });
    out += rect(0, H - 12, W, 12, p.b, { op: .7 });
    return out;
  },

  /* a beam and columns — proportion, rule, what is weighed */
  scale: function (r, p, o) {
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const cols = span(o, 3, 6);
    for (let i = 0; i < cols; i++) {
      const h = num(r, 34, 108), w = W / cols - 14;
      out += rect(W / cols * i + 7, H - h - 18, w, h, i % 2 ? p.b : INK,
                  { r: 5, op: i % 2 ? .9 : .6 });
    }
    const by = num(r, 34, 56);
    out += line(10, by, W - 10, by + num(r, -14, 14), RED, { sw: 2.2 });
    out += circ(W / 2, by, 5, INK, { op: .8 });
    return out;
  },

  /* three large primaries — the first years, before anything is specialised */
  blocks: function (r, p) {
    let out = rect(0, 0, W, H, p.a, { op: .34 });
    out += circ(num(r, 56, 84), num(r, 64, 94), num(r, 32, 40), p.b,
                { stroke: INK, sw: 1.6, op: .95 });
    out += rect(num(r, W * .42, W * .56), num(r, 30, 50), num(r, 60, 78), num(r, 60, 78),
                "none", { r: 10, stroke: INK, sw: 1.8, rot: num(r, -10, 10), op: .7 });
    const tx = num(r, W * .62, W * .78), ty = num(r, H * .6, H * .76), s = num(r, 24, 34);
    out += path("M" + n2(tx) + " " + n2(ty - s) + " L" + n2(tx + s) + " " + n2(ty + s * .6)
              + " L" + n2(tx - s) + " " + n2(ty + s * .6) + "Z", { fill: RED });
    return out;
  },

  /* columns under an arc — the general, academic route */
  columns: function (r, p, o) {
    let out = rect(0, 0, W, H, p.a, { op: WASH });
    const cnt = span(o, 3, 6), top = num(r, 60, 74);
    out += path("M18 " + n2(top) + " Q" + n2(W / 2) + " " + n2(top - 48) + " " + n2(W - 18)
              + " " + n2(top), { stroke: INK, sw: 2, op: .8 });
    for (let i = 0; i < cnt; i++) {
      const x = 18 + (W - 36) / (cnt - 1) * i;
      out += rect(x - 6, top, 12, H - top - 16, i === cnt - 2 ? RED : p.b,
                  { r: 4, stroke: INK, sw: 1.2, op: .95 });
    }
    out += rect(10, H - 16, W - 20, 7, INK, { r: 4, op: .7 });
    return out;
  },
} satisfies Record<string, Motif>;

/* An unknown motif falls back to the grid rather than drawing nothing: a
   blank tile reads as a broken page, a generic one reads as a plain subject. */
function motifOf(name: string): Motif {
  return (MOTIFS as Record<string, Motif>)[name] ?? MOTIFS.grid;
}

/* ---------- what belongs to what ----------

   Mapped by meaning, not by department. Two subjects may share a motif — the
   palette of the branch and the seed of the id still keep them apart. */

const BY_SUBJECT: Record<string, string> = {
  matematika: "grid",      informatika: "flow",     programovani: "flow",
  databaze: "flow",        site: "circuit",         elektro: "circuit",
  dejepis: "strata",       zemepis: "contour",      vlastiveda: "contour",
  fyzika: "orbit",         chemie: "lattice",
  prirodopis: "branch",    prirodoveda: "branch",   prvouka: "branch",
  cesky: "weave",          anglictina: "wave",      nemcina: "wave",
  jazyky: "wave",          hudebni: "wave",
  vytvarna: "planes",      telesna: "arcs",
  zdravi: "pulse",         obcanska: "scale",       zsv: "scale",
  pracovni: "grid",
};

const BY_AREA: Record<string, string> = { humanitni: "weave", prirodni: "lattice", technicka: "circuit" };

/* Only the strand's own tile uses this. Everything under a strand takes the
   motif of its subject, which is what keeps a subject recognisable across the
   years and across the strands that teach it. */
const BY_STRAND_MOTIF: Record<string, string> = {
  zs1: "blocks", zs2: "grid", gym: "columns", odborna: "circuit",
};

/* ---------- composition ---------- */

function shell(body: string, cls?: string): string {
  return '<svg class="cover' + (cls ? " " + cls : "") + '" viewBox="0 0 ' + W + " " + H + '"'
    + ' xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice"'
    + ' role="img" aria-hidden="true" focusable="false">' + body + "</svg>";
}

function compose(seed: string, motif: string, strandId: string | undefined, opts: Opts): string {
  const r = rngOf(seedOf(seed));
  const p = paletteOf(strandId);
  const fn = motifOf(motif);
  return shell(rect(0, 0, W, H, PAPER) + fn(r, p, opts) + (opts.after ? opts.after(r, p) : ""),
               opts.cls);
}

/* The numeral is the whole point of a year tile, so it is drawn into the art
   rather than left to the label: a knocked-out figure over a quietened motif.
   Everywhere else the art carries no text at all — the tile already prints
   the title underneath, and setting it twice is noise, not emphasis. */
function numeral(value: number): (r: Rng, p: Palette) => string {
  return function () {
    return rect(0, 0, W, H, PAPER, { op: .5 })
      + '<text x="' + (W - 20) + '" y="' + (H - 24) + '" text-anchor="end"'
      + ' fill="' + INK + '" font-family="var(--sans)" font-size="112" font-weight="900"'
      + ' letter-spacing="-.06em">' + value + "</text>"
      + rect(20, H - 40, 40, 7, RED, { r: 3.5 });
  };
}

/* ---------- public ---------- */

export function strand(id: string): string {
  return compose("strand:" + id, BY_STRAND_MOTIF[id] ?? "grid", id, { cls: "cover-strand" });
}

/* A subject stands above the years, so it takes the middle of the density
   range: it is the series, not one season of it. */
export function subject(id: string, branch?: Branch, subjects?: Record<string, Subject>): string {
  const s = subjects?.[id];
  const motif = BY_SUBJECT[id] ?? (s ? BY_AREA[s.area] : undefined) ?? "grid";
  return compose("subject:" + id + ":" + (branch?.strand ?? ""), motif, branch?.strand,
                 { cls: "cover-subject" });
}

export function year(branch: Branch, subjectId: string, y: number): string {
  const motif = BY_SUBJECT[subjectId] ?? "grid";
  return compose("year:" + branch.strand + ":" + subjectId + ":" + y, motif, branch.strand,
                 { cls: "cover-year", d: densityOf(y, branch.years), after: numeral(y) });
}

export function material(ex: Partial<IndexEntry>, branch?: Branch): string {
  /* A material taught at both a gymnázium and an odborná škola has two
     branches, and the one that decides is the column it is being seen in. Off
     the catalogue — on the material's own page — the first of its strands
     decides, which at least never changes under the reader. */
  const strandId = branch?.strand ?? ex.strand?.[0];
  const motif = BY_SUBJECT[ex.subject ?? ""] ?? "grid";
  /* The red foot under the composition used to mark the two application
     shapes apart from a walk through the method. Every material is a lesson
     now and every lesson has an application in its kit, so the mark told the
     reader nothing and had already stopped being drawn — no index entry has
     carried a shape since item 12. */
  return compose("material:" + ex.id, motif, strandId, {
    cls: "cover-material",
    d: densityOf(ex.year, branch?.years),
  });
}

export const covers = { strand, subject, year, material };
